import { useState, useEffect, useCallback } from "react";
import { searchWords, getLetterStats, type ScoredWord, type LetterPosition } from "../services/wordAnalyzer";
import { LETTER_SCORES } from "../utils/scrabble";
import { useI18n } from "../i18n/I18nContext";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const HIGH_VALUE_LETTERS = ["Q", "Z", "X", "J", "K"];

export default function WordFinder() {
  const { t } = useI18n();
  const [selectedLetter, setSelectedLetter] = useState<string>("Z");
  const [position, setPosition] = useState<LetterPosition>("anywhere");
  const [minLength, setMinLength] = useState<number>(2);
  const [maxLength, setMaxLength] = useState<number>(15);
  const [results, setResults] = useState<ScoredWord[]>([]);
  const [stats, setStats] = useState({ totalWords: 0, averageScore: 0, highestScore: 0 });
  const [loading, setLoading] = useState(false);

  const performSearch = useCallback(() => {
    if (!selectedLetter) return;

    setLoading(true);
    setTimeout(() => {
      const words = searchWords({
        letter: selectedLetter,
        position,
        minLength,
        maxLength,
        limit: 10
      });
      const letterStats = getLetterStats(selectedLetter);
      setResults(words);
      setStats(letterStats);
      setLoading(false);
    }, 50);
  }, [selectedLetter, position, minLength, maxLength]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const letterValue = LETTER_SCORES[selectedLetter] || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-page via-surface to-page py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold font-heading text-primary mb-3">{t('wordFinderTitle')}</h1>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            {t('wordFinderSubtitle')}
          </p>
        </div>

        {/* Strategy Tip */}
        <div className="bg-surface/80 backdrop-blur-theme border border-subtle p-4 mb-8 rounded-card">
          <h2 className="font-semibold text-primary mb-2">💡 {t('wordFinderProTipTitle')}</h2>
          <p className="text-secondary text-sm">
            {t('wordFinderProTipText')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Search Controls */}
          <div className="lg:col-span-2">
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6">
              <h2 className="text-xl font-semibold text-primary mb-6">{t('wordFinderFiltersTitle')}</h2>

              {/* Quick Select - High Value Letters */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-secondary mb-3">{t('wordFinderQuickSelectTitle')}</h3>
                <div className="flex gap-2 flex-wrap">
                  {HIGH_VALUE_LETTERS.map(letter => (
                    <button
                      key={letter}
                      onClick={() => setSelectedLetter(letter)}
                      className={`px-4 py-2 rounded-lg font-bold transition-all ${
                        selectedLetter === letter
                          ? "bg-finder text-white shadow-md scale-105"
                          : "bg-surface-raised text-secondary hover:bg-surface-raised/80"
                      }`}
                    >
                      {letter}
                      <span className="text-sm ml-1">({LETTER_SCORES[letter]}{t('wordValidatorPointsSuffix')})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* All Letters Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary mb-2">
                  {t('wordFinderSelectLetterLabel')}
                </label>
                <select
                  value={selectedLetter}
                  onChange={(e) => setSelectedLetter(e.target.value)}
                  className="w-full border-2 border-input-border bg-input-bg rounded-lg px-4 py-2 focus:outline-none focus:border-finder transition-colors text-primary"
                >
                  {ALPHABET.map(letter => (
                    <option key={letter} value={letter}>
                      {letter} ({LETTER_SCORES[letter]}{t('wordValidatorPointsSuffix')})
                    </option>
                  ))}
                </select>
              </div>

              {/* Position Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary mb-3">
                  {t('wordFinderLetterPositionLabel')}
                </label>
                <div className="flex gap-3">
                  {(["anywhere", "starts", "ends"] as LetterPosition[]).map(pos => (
                    <label
                      key={pos}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="position"
                        value={pos}
                        checked={position === pos}
                        onChange={(e) => setPosition(e.target.value as LetterPosition)}
                        className="mr-2 w-4 h-4 text-finder focus:ring-finder"
                      />
                      <span className="text-secondary">
                        {pos === 'anywhere'
                          ? t('wordFinderPositionAnywhere')
                          : pos === 'starts'
                            ? t('wordFinderPositionStarts')
                            : t('wordFinderPositionEnds')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Length Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-secondary mb-3">
                  {t('wordFinderWordLengthLabel', { min: minLength, max: maxLength })}
                </label>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted">{t('wordFinderMinLabel', { value: minLength })}</label>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={minLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setMinLength(val);
                        if (val > maxLength) setMaxLength(val);
                      }}
                      className="w-full h-2 bg-surface-raised rounded-lg appearance-none cursor-pointer accent-finder"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted">{t('wordFinderMaxLabel', { value: maxLength })}</label>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={maxLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setMaxLength(val);
                        if (val < minLength) setMinLength(val);
                      }}
                      className="w-full h-2 bg-surface-raised rounded-lg appearance-none cursor-pointer accent-finder"
                    />
                  </div>
                </div>
              </div>

              {/* Common Presets */}
              <div>
                <h3 className="text-sm font-medium text-secondary mb-3">{t('wordFinderQuickPresetsTitle')}</h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => { setMinLength(2); setMaxLength(2); }}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    {t('wordFinderPresetTwoLetter')}
                  </button>
                  <button
                    onClick={() => { setMinLength(7); setMaxLength(7); }}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    {t('wordFinderPresetBingo')}
                  </button>
                  <button
                    onClick={() => { setMinLength(2); setMaxLength(15); }}
                    className="px-3 py-1 text-sm bg-surface-raised hover:bg-surface-raised/80 rounded-lg transition-colors"
                  >
                    {t('wordFinderPresetAll')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-primary mb-4">
                {t('wordFinderSidebarTitle')} <span className="text-3xl text-finder-text">{selectedLetter}</span>
              </h3>

              <div className="space-y-4">
                <div className="border-b border-subtle pb-3">
                  <div className="text-sm text-muted">{t('wordFinderStatPointValue')}</div>
                  <div className="text-2xl font-bold text-finder-text">{letterValue}</div>
                </div>

                <div className="border-b border-subtle pb-3">
                  <div className="text-sm text-muted">{t('wordFinderStatTotalWords')}</div>
                  <div className="text-2xl font-bold text-primary">{stats.totalWords.toLocaleString()}</div>
                </div>

                <div className="border-b border-subtle pb-3">
                  <div className="text-sm text-muted">{t('wordFinderStatHighestScore')}</div>
                  <div className="text-2xl font-bold text-primary">{stats.highestScore}</div>
                </div>

                <div>
                  <div className="text-sm text-muted">{t('wordFinderStatAverageScore')}</div>
                  <div className="text-2xl font-bold text-primary">{stats.averageScore}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mt-6">
          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle shadow-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-primary">
                {t('wordFinderResultsTitle')}
              </h2>
              {loading && (
                <span className="text-sm text-muted">{t('wordFinderLoading')}</span>
              )}
            </div>

            {results.length === 0 ? (
              <div className="text-center py-12 text-muted">
                <p className="text-lg">{t('wordFinderNoWordsTitle')}</p>
                <p className="text-sm mt-2">{t('wordFinderNoWordsHint')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-subtle">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">{t('wordFinderTableRank')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">{t('wordFinderTableWord')}</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">{t('wordFinderTableLength')}</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-secondary">{t('wordFinderTableScore')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((word, index) => (
                      <tr
                        key={`${word.word}-${index}`}
                        className="border-b border-subtle hover:bg-finder-subtle/40 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-finder-subtle text-finder-text font-semibold text-sm">
                            {index + 1}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono font-bold text-lg text-primary">
                            {word.word}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-secondary">
                          {word.length}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="inline-block bg-finder text-white font-bold px-3 py-1 rounded-lg">
                            {word.score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
